import { yupResolver } from '@hookform/resolvers/yup'
import { type Resolver } from 'react-hook-form'

import { getAuthenticationFormValidationSchema } from 'components/status-checks/authentication-form-schema-builders'
import { StatusCheckFormStep } from 'components/status-checks/create/create-status-check-utils'
import { useGetCategoryFieldVisibilityState } from 'components/status-checks/hooks/use-get-category-field-visibility-state'
import { getStatusCheckFormValidationSchema } from 'components/status-checks/status-checks-form-schema-builders'
import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { useObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useFeatureFlags } from 'hooks/use-feature-flags'
import {
  type ServiceStatusCheckFromStatusCheck,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'

/**
 * Provides a builder function which, given the available external integrations and status checks,
 * returns the proper validation schema for the Status Check form.
 * @returns A resolver function used by yup which constructs the schema given the provided form values and context.
 */
export function useStatusCheckFormValidationSchemaBuilder(
  externalIntegrations: TeamExternalIntegration[],
  statusChecks: ServiceStatusCheckFromStatusCheck[]
): Resolver<StatusCheckFormValues, StatusCheckFormStep> {
  const getCategoryFieldVisibilityState = useGetCategoryFieldVisibilityState()
  const featureFlags = useFeatureFlags()
  const { isCreatingAuthenticationOnly } =
    useObservabilityToolAuthenticationContext()

  return function (values, step, resolverOptions) {
    // short-circuit a bunch of derivations in the event we are on the create authentication step
    if (step === StatusCheckFormStep.Authentication) {
      const schema = getAuthenticationFormValidationSchema(
        externalIntegrations,
        isCreatingAuthenticationOnly
      )
      return yupResolver(schema)(values, step, resolverOptions)
    }

    const { dynatraceSelector, observabilityTool } = values
    const categoryVisibilityState = getCategoryFieldVisibilityState(
      observabilityTool,
      dynatraceSelector?.type
    )

    // As part of the creation flow, we want to take into consideration the active form step,
    // and conditionally build the appropriate schema.
    const schema = getStatusCheckFormValidationSchema(
      externalIntegrations,
      statusChecks,
      { categoryVisibilityState, featureFlags }
    )

    return yupResolver(schema)(values, step, resolverOptions)
  }
}
